import styledC, { css } from 'styled-components';

import { CombinedTheme, InputDesignTokens } from '@adsk/alloy-react-styles';
import theme from '@adsk/alloy-react-theme';

type StyledDivProps = {
  $disabled: boolean;
  $error: boolean;
  $isWeave: boolean;
  $halo?: Partial<NonNullable<CombinedTheme['tokens']>['semantic']>['halo'];
  $tokens?: InputDesignTokens;
};

type StyledInputProps = {
  $isWeave: boolean;
  $tokens?: InputDesignTokens;
};

export const StyledDiv = styledC.div<StyledDivProps>`
  font-family: ${({ $tokens }) => $tokens?.fontFamily};
  font-weight: ${({ $tokens }) => $tokens?.fontWeight};
  font-size: ${({ $tokens }) => $tokens?.fontSize};

  box-sizing: border-box;
  ${({ $isWeave, $tokens }) => {
    const minHeight = $tokens?.minHeight;
    return $isWeave ? `min-height: ${minHeight}` : `height: ${minHeight}`;
  }};
  position: relative;
  width: 100%;
  color: ${({ $tokens }) => $tokens?.color};
  border-style: ${({ $tokens }) => $tokens?.box?.borderStyle};
  border-width: ${({ $tokens }) => $tokens?.box?.borderWidth};
  border-bottom-style: ${({ $tokens }) => $tokens?.borderBottomStyle};
  border-bottom-width: ${({ $tokens }) => $tokens?.borderBottomWidth};
  border-top-left-radius: ${({ $tokens }) => $tokens?.borderTopLeftRadius};
  border-top-right-radius: ${({ $tokens }) => $tokens?.borderTopRightRadius};
  border-bottom-left-radius: ${({ $tokens }) => $tokens?.borderBottomLeftRadius};
  border-bottom-right-radius: ${({ $tokens }) => $tokens?.borderBottomRightRadius};

  padding-left: ${({ $tokens }) => $tokens?.box?.paddingLeft};
  padding-right: ${({ $tokens }) => $tokens?.box?.paddingRight};

  ${({ $tokens, $error }) => {
    const errorColor = $tokens?.error?.borderBottomColor;
    const baseColor = $tokens?.box?.borderColor;
    const transition = theme.transitions.getTransitionsFromConfig({
      'transition-duration': $tokens?.transitionDuration,
      'transition-property': $tokens?.transitionProperty,
      'transition-timing-function': $tokens?.transitionTimingFunction,
    });
    return css`
      transition: ${transition};
      border-color: ${$error ? errorColor : baseColor};
    `;
  }}


  ${({ $error, $isWeave, $disabled, $tokens, $halo: $halos }) => {
    const halos = $halos?.default;
    const borderColors = $tokens?.borderBottomColor;
    const boxShadows = $tokens?.boxShadow;
    const bgColor = $tokens?.box?.backgroundColor;
    const selectedBgColor = $error
      ? $tokens?.error?.backgroundColor?.selected
      : $tokens?.backgroundColor?.selected;

    if (!$isWeave) {
      return css`
        box-shadow: ${$error ? boxShadows : 'none'};

        ${!$disabled &&
        css`
          background-color: ${bgColor};

          &:hover {
            box-shadow: ${halos?.hover};
          }

          &:focus-within,
          &:focus {
            box-shadow: ${halos?.focus};
          }

          &:active {
            box-shadow: ${halos?.pressed};
          }
        `}

        ${$disabled &&
        css`
          background-color: ${$tokens?.disabled?.backgroundColor};
          border-color: ${$tokens?.disabled?.borderColor};
          color: ${$tokens?.disabled?.color};
          cursor: not-allowed;
          box-shadow: none;
          pointer-events: none;
        `}
      `;
    }

    return css`
      ${!$disabled &&
      css`
        background-color: ${bgColor};

        &:hover {
          border-bottom-color: ${!$error && borderColors?.hover};
          box-shadow: ${$error ? 'none' : boxShadows?.hover};
        }

        &:focus-within:not(:active),
        &:focus:not(:active) {
          border-bottom-color: ${!$error && borderColors?.focus};
          box-shadow: ${$error ? 'none' : boxShadows?.focus};
        }

        ::selection {
          background-color: ${selectedBgColor};
        }
      `}

      ${$disabled &&
      css`
        background-color: ${bgColor};
        opacity: ${$tokens?.opacity?.disabled};
        cursor: not-allowed;
        box-shadow: none;
        pointer-events: none;
      `}
    `;
  }}
`;

export const StyledInput = styledC.input<StyledInputProps>`
  font-family: ${({ $tokens }) => $tokens?.fontFamily};
  font-weight: ${({ $tokens }) => $tokens?.fontWeight};
  font-size: ${({ $tokens }) => $tokens?.fontSize};

  line-height: ${({ $tokens }) => $tokens?.lineHeight};
  height: 100%;
  left: 0px;
  outline: none;
  padding: 0px 8px 1px;
  position: absolute;
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ $tokens }) => $tokens?.color};

  &::placeholder {
    color: ${({ $tokens }) => $tokens?.placeholder?.color};
  }

  &:disabled {
    color: ${({ $tokens }) => $tokens?.color};
    opacity: ${({ $tokens }) => $tokens?.opacity?.disabled};
    background-color: transparent;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
